/**
 * DataTable row-rendering sub-components: the normal (document-flow) row path, the
 * virtualized (absolutely-positioned) row path, and the dispatcher that picks between them.
 * Split out of data-table-helpers.tsx to keep that file within the max-lines lint budget.
 */
import * as React from 'react';
import type { VirtualItem } from '@tanstack/react-virtual';
import { cn } from '../lib/utils.js';
import { alignClass, resolveCellClassName, resolveCellStyle } from './data-table-hooks.js';
import type { ColumnDef } from './data-table.js';

// ─── Row cells (shared between the normal and virtualized row paths) ──────────

/** Render a row's `<td>` cells for the given columns. Shared by both render paths. */
export function renderRowCells<T>(
  item: T,
  columns: ColumnDef<T>[],
  resolveWidth: (colId: string, colWidth?: number) => number | undefined,
): React.ReactNode {
  return columns.map((col) => {
    const w = resolveWidth(col.id, col.width);
    return (
      <td
        key={col.id}
        style={resolveCellStyle(w, col.cellStyle?.(item))}
        className={cn(
          resolveCellClassName(col.cellClassName, item) ?? 'px-4 py-3',
          'whitespace-nowrap',
          alignClass(col.align),
          w && 'overflow-hidden',
        )}
      >
        {col.renderCell(item)}
      </td>
    );
  });
}

// ─── Virtualized rows ──────────────────────────────────────────

interface RowPropsResult {
  isClickable: boolean;
  className: string;
  onClick?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

interface VirtualizedRowsProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  virtualItems: VirtualItem[];
  getRowKey: (item: T) => string;
  getRowTestId?: (item: T) => string;
  getRowAttributes?: (item: T) => Record<string, string | undefined>;
  resolveWidth: (colId: string, colWidth?: number) => number | undefined;
  buildRowProps: (item: T, idx: number) => RowPropsResult;
  renderRowLinkCell?: (item: T) => React.ReactNode;
  renderSelectionCell?: (item: T) => React.ReactNode;
}

/** Renders only `virtualItems` as absolutely-positioned `<tr>`s inside a virtualized `TableBody`. */
export function VirtualizedRows<T>({
  columns,
  data,
  virtualItems,
  getRowKey,
  getRowTestId,
  getRowAttributes,
  resolveWidth,
  buildRowProps,
  renderRowLinkCell,
  renderSelectionCell,
}: VirtualizedRowsProps<T>) {
  return (
    <>
      {virtualItems.map((virtualRow) => {
        const item = data[virtualRow.index];
        if (!item) return null;
        const { isClickable, className, onClick, onKeyDown } = buildRowProps(
          item,
          virtualRow.index,
        );
        return (
          <tr
            key={getRowKey(item)}
            data-testid={getRowTestId?.(item)}
            tabIndex={isClickable ? 0 : undefined}
            onClick={onClick}
            onKeyDown={onKeyDown}
            className={className}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualRow.start}px)`,
              display: 'table',
              tableLayout: 'fixed',
            }}
            {...getRowAttributes?.(item)}
          >
            {renderRowLinkCell?.(item)}
            {renderSelectionCell?.(item)}
            {renderRowCells(item, columns, resolveWidth)}
          </tr>
        );
      })}
    </>
  );
}

// ─── Normal (non-virtualized) rows ────────────────────────────

interface NormalRowsProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  getRowKey: (item: T) => string;
  getRowTestId?: (item: T) => string;
  getRowAttributes?: (item: T) => Record<string, string | undefined>;
  resolveWidth: (colId: string, colWidth?: number) => number | undefined;
  buildRowProps: (item: T, idx: number) => RowPropsResult;
  renderRowLinkCell?: (item: T) => React.ReactNode;
  renderSelectionCell?: (item: T) => React.ReactNode;
  draggableRows?: boolean;
  onRowDragStart: (e: React.DragEvent, idx: number) => void;
  onRowDragOver: (e: React.DragEvent, idx: number) => void;
  onRowDrop: (e: React.DragEvent, idx: number) => void;
  onRowDragEnd: () => void;
}

/** Renders every row of `data` in normal document flow (pre-virtualization behavior). */
export function NormalRows<T>({
  columns,
  data,
  getRowKey,
  getRowTestId,
  getRowAttributes,
  resolveWidth,
  buildRowProps,
  renderRowLinkCell,
  renderSelectionCell,
  draggableRows,
  onRowDragStart,
  onRowDragOver,
  onRowDrop,
  onRowDragEnd,
}: NormalRowsProps<T>) {
  return (
    <>
      {data.map((item, idx) => {
        const { isClickable, className, onClick, onKeyDown } = buildRowProps(item, idx);
        return (
          <tr
            key={getRowKey(item)}
            draggable={draggableRows}
            onDragStart={draggableRows ? (e) => onRowDragStart(e, idx) : undefined}
            onDragOver={draggableRows ? (e) => onRowDragOver(e, idx) : undefined}
            onDrop={draggableRows ? (e) => onRowDrop(e, idx) : undefined}
            onDragEnd={draggableRows ? onRowDragEnd : undefined}
            className={className}
            data-testid={getRowTestId?.(item)}
            tabIndex={isClickable ? 0 : undefined}
            onClick={onClick}
            onKeyDown={onKeyDown}
            {...getRowAttributes?.(item)}
          >
            {renderRowLinkCell?.(item)}
            {renderSelectionCell?.(item)}
            {renderRowCells(item, columns, resolveWidth)}
          </tr>
        );
      })}
    </>
  );
}

// ─── Table rows dispatcher ─────────────────────────────────────

interface TableRowsProps<T> extends NormalRowsProps<T> {
  virtualizer?: { virtualItems: VirtualItem[] };
}

/** Picks the virtualized or normal row-rendering path based on `virtualizer` presence. */
export function TableRows<T>({ virtualizer, ...rest }: TableRowsProps<T>) {
  if (virtualizer) {
    return <VirtualizedRows {...rest} virtualItems={virtualizer.virtualItems} />;
  }
  return <NormalRows {...rest} />;
}
